import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Image as ImageIcon, Upload, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { settingsApi } from '../../lib/api';

const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");
const HEADER_IMG_URL = `${BACKEND_URL}/api/settings/logos/public_header_bg`;

/**
 * Konfiguriert das Hintergrundbild + den farbigen Overlay für den
 * öffentlichen Seminar-Header. Settings (Overlay-Farbe, Transparenz,
 * Parallax, Aktiv-Schalter) werden zusammen mit den Design-Farben
 * über das bestehende Design-API gespeichert (= reisen mit jedem
 * Color-Preset mit). Das Bild selbst wird wie ein Logo über
 * /api/settings/logos/public_header_bg verwaltet.
 */
export function PublicHeaderImageCard({ colors, setColors }) {
    const [imgPresent, setImgPresent] = useState(false);
    const [imgVersion, setImgVersion] = useState(0); // cache-bust
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        // Existiert bereits ein Header-Bild?
        api.get('/settings/logos').then(r => {
            setImgPresent(!!r.data?.public_header_bg);
        }).catch(() => {});
    }, [imgVersion]);

    const overlay = colors.public_header_overlay_color || '#1F2937';
    const opacity = Math.max(0, Math.min(100, parseInt(colors.public_header_overlay_opacity ?? 60, 10)));
    const parallax = !!colors.public_header_parallax;
    const enabled = !!colors.public_header_image_enabled;
    const extendTopbar = !!colors.public_header_extend_topbar;
    const titleText = colors.public_header_title ?? 'Fort- & Weiterbildungen';
    const subtitleText = colors.public_header_subtitle ?? 'Entdecken Sie unsere Seminare der BGPR-Akademie sowie Angebote externer Partner für Herzgruppenleiter.';

    const update = (patch) => setColors(prev => ({ ...prev, ...patch }));

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Datei zu groß (max. 5 MB)');
            return;
        }
        setUploading(true);
        try {
            await settingsApi.uploadLogo('public_header_bg', file);
            // Bild-Switch automatisch aktivieren beim ersten Upload
            update({ public_header_image_enabled: true });
            setImgVersion(v => v + 1);
            toast.success('Header-Bild hochgeladen');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Header-Bild wirklich entfernen?')) return;
        try {
            await api.delete('/settings/logos/public_header_bg');
            update({ public_header_image_enabled: false });
            setImgVersion(v => v + 1);
            toast.success('Header-Bild entfernt');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
    };

    return (
        <Card data-testid="public-header-image-card">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-base">
                    <ImageIcon className="h-4 w-4" />
                    Öffentlicher Header (Hintergrundbild &amp; Overlay)
                </CardTitle>
                <CardDescription>
                    Optionales Hintergrundbild für den Hero-Bereich der öffentlichen Seminar-Übersicht und der Detailseite.
                    Overlay-Farbe und Transparenz lassen die Headline lesbar bleiben. Diese Einstellungen werden mit dem
                    aktuellen Farbschema gespeichert – jede Vorlage kann also einen eigenen Header-Look haben.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                    {/* Vorschau */}
                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Vorschau</Label>
                        <div
                            className="relative h-44 rounded-lg overflow-hidden border"
                            style={{
                                backgroundColor: colors.secondary || '#1F2937',
                                backgroundImage: enabled && imgPresent ? `url(${HEADER_IMG_URL}?v=${imgVersion})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                            data-testid="public-header-preview"
                        >
                            {enabled && imgPresent && (
                                <div
                                    className="absolute inset-0"
                                    style={{ backgroundColor: overlay, opacity: opacity / 100 }}
                                />
                            )}
                            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
                                <h3 className="text-white text-xl font-bold drop-shadow">{titleText || 'Fort- & Weiterbildungen'}</h3>
                                <p className="text-white/80 text-xs mt-1 drop-shadow line-clamp-2">{subtitleText || 'Beispiel-Hero auf der öffentlichen Seite'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Upload + Schalter */}
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs text-muted-foreground">Bild</Label>
                            <div className="flex gap-2 mt-1">
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    className="hidden"
                                    onChange={handleUpload}
                                    data-testid="header-bg-input"
                                />
                                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="header-bg-upload">
                                    {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                                    {imgPresent ? 'Bild ersetzen' : 'Bild hochladen'}
                                </Button>
                                {imgPresent && (
                                    <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700" data-testid="header-bg-delete">
                                        <Trash2 className="h-4 w-4 mr-1.5" />
                                        Entfernen
                                    </Button>
                                )}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">PNG, JPG oder WebP · max. 5 MB · empfohlen mind. 1920×600 px</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={enabled}
                                onCheckedChange={(v) => update({ public_header_image_enabled: v })}
                                disabled={!imgPresent}
                                data-testid="header-bg-toggle"
                            />
                            <Label className="text-sm">Header-Bild verwenden</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={parallax}
                                onCheckedChange={(v) => update({ public_header_parallax: v })}
                                disabled={!enabled}
                                data-testid="header-parallax-toggle"
                            />
                            <Label className="text-sm">Parallax-Effekt (fixierter Hintergrund)</Label>
                        </div>
                    </div>
                </div>

                {/* Overlay-Steuerung */}
                <div className="grid md:grid-cols-2 gap-5 border-t pt-5">
                    <div>
                        <Label className="text-xs text-muted-foreground">Overlay-Farbe</Label>
                        <div className="flex items-center gap-3 mt-1">
                            <input
                                type="color"
                                value={overlay}
                                onChange={(e) => update({ public_header_overlay_color: e.target.value })}
                                className="h-9 w-14 rounded border cursor-pointer"
                                data-testid="header-overlay-color"
                            />
                            <input
                                type="text"
                                value={overlay}
                                onChange={(e) => update({ public_header_overlay_color: e.target.value })}
                                className="border rounded px-2 py-1 text-sm font-mono w-28"
                                pattern="^#[0-9A-Fa-f]{6}$"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">
                            Overlay-Transparenz: <span className="font-mono font-semibold text-foreground">{opacity}%</span>
                        </Label>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={opacity}
                            onChange={(e) => update({ public_header_overlay_opacity: parseInt(e.target.value, 10) })}
                            className="w-full mt-2 accent-primary"
                            data-testid="header-overlay-opacity"
                        />
                        <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                            <span>0% (Bild pur)</span>
                            <span>100% (volle Farbe)</span>
                        </div>
                    </div>
                </div>

                {/* Layout-Erweiterung */}
                <div className="border-t pt-5">
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={extendTopbar}
                            onCheckedChange={(v) => update({ public_header_extend_topbar: v })}
                            disabled={!enabled}
                            data-testid="header-extend-topbar"
                        />
                        <Label className="text-sm">Bild bis in die Logo-Leiste ausdehnen (transparenter Top-Header)</Label>
                    </div>
                </div>

                {/* Hero-Texte */}
                <div className="border-t pt-5 space-y-3">
                    <div>
                        <Label className="text-xs text-muted-foreground">Headline</Label>
                        <input
                            type="text"
                            maxLength={120}
                            value={titleText}
                            onChange={(e) => update({ public_header_title: e.target.value })}
                            className="w-full mt-1 border rounded px-3 py-1.5 text-sm"
                            placeholder="Fort- & Weiterbildungen"
                            data-testid="header-title-input"
                        />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">Beschreibungstext</Label>
                        <textarea
                            rows={2}
                            maxLength={300}
                            value={subtitleText}
                            onChange={(e) => update({ public_header_subtitle: e.target.value })}
                            className="w-full mt-1 border rounded px-3 py-1.5 text-sm"
                            placeholder="Entdecken Sie unsere Seminare …"
                            data-testid="header-subtitle-input"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">{subtitleText.length}/300 Zeichen · Speichern erfolgt zusammen mit den Farben.</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
